import { cloneDeep, merge, snakeCase } from 'lodash'
import VueI18n from 'vue-i18n'

import { VueComponent } from "../../shared/types/VueComponent";
import { SelectOptionData } from '../Pi/types'
import { BotProperty, GroupedBotProperty, PropertyFieldType } from './components/Properties/types'

type BotPropertyTypeInfo = {
  path: string
  type: string
  properties?: Array<BotPropertyTypeInfo>
  readOnly?: boolean
}

type BotPropertiesFrontendInfo = {
  icon: string,
  settingView?: VueComponent
  userTablePropertyRowView?: VueComponent
}

type BotPropertiesExtra = Partial<Record<PropertyFieldType | string, BotPropertiesFrontendInfo>>

export let BotPropertyTypes: Array<BotPropertyTypeInfo> = []

const BotPropertiesExtraData: BotPropertiesExtra = {
  [PropertyFieldType.Number]: {
    icon: 'pin'
  },
  [PropertyFieldType.Double]: {
    icon: 'pin'
  },
  [PropertyFieldType.String]: {
    icon: 'text_fields'
  },
  [PropertyFieldType.Time]: {
    icon: 'schedule',
  },
  [PropertyFieldType.Phone]: {
    icon: 'phone_iphone',
  },
  [PropertyFieldType.Email]: {
    icon: 'email',
  },
  [PropertyFieldType.Flag]: {
    icon: 'flag',
  },
  [PropertyFieldType.Date]: {
    icon: 'event',
  },
  [PropertyFieldType.DateTime]: {
    icon: 'pending_actions',
  },
  [PropertyFieldType.URL]: {
    icon: 'wifi_tethering',
  },
}

export function initBotProperties(properties: Array<BotPropertyTypeInfo>, extraData?: BotPropertiesExtra) {
  BotPropertyTypes = properties

  if (extraData) {
    merge(BotPropertiesExtraData, extraData)
  }
}

export const isPropertyEditable = (type: PropertyFieldType) => {
  const p = BotPropertyTypes.find(p => p.path === type)

  if (p && p.readOnly !== undefined) {
    return !p.readOnly
  }

  return false
}

export const getNewPropertyIcon = (type: string): string => {
  let icon = ''

  function findType(a: BotPropertyTypeInfo) {
    if (a.path === type) {
      icon = BotPropertiesExtraData?.[type]?.icon ?? ''

      return true;
    }

    return Array.isArray(a.properties) && a.properties.some(findType);
  }

  if (!type) {
    return icon
  }

  BotPropertyTypes.some(findType);

  return icon
}

export function sortProperties(properties: Array<GroupedBotProperty> | Array<BotProperty>) {
  return cloneDeep(properties).sort((a, b) => b.id ?? 0 - (a.id ?? 0))
}

export function getPropertyTranslation<T extends string>(i18n: VueI18n, type: T) {
  const lcType = type.toLowerCase();
  const scType = snakeCase(lcType)

  return i18n.te(`${ scType }_option`) ? i18n.t(`${ scType }_option`).toString() : type.toString()
}

export function getPropertiesByType<T extends BotProperty>(properties: Array<T>, types: Array<PropertyFieldType>): Array<T> {
  const result: Array<T> = []

  function checkType(value:any) {
    if (types.includes(value.type)) {
      result.push(value)
    }

    Array.isArray(value.properties) && value.properties.forEach(checkType);
  }

  properties.forEach(checkType);

  return result
}

export function setPlaceholderPath<T extends BotProperty>(properties: Array<T>): Array<T & {placeholderPath: string}> {
  function propertyOption<T extends BotProperty>(properties: Array<T>, parent?: string): Array<T & {placeholderPath: string}> {
    return properties
      .map(p => {
        const valueWithParent = parent ? parent + ' -> ' + p.name : p.name ?? p.type

        return {
          ...p,
          placeholderPath: valueWithParent,
          properties: 'properties' in p && p.properties.length ? propertyOption(p.properties, valueWithParent) : []
        }
      })
  }

  return propertyOption(properties)
}

export function hierarchyPlaceholderProperties<T extends BotProperty>(i18n: VueI18n, properties: Array<T>): Array<any> {
  const setItem = (properties: Array<any>): Array<any> => {
    return properties
      .map(p => {
        return {
          label: getPropertyTranslation(i18n, p.name ?? p.type),
          value: `{${ p.placeholderPath }}`,
          children: 'properties' in p && p.properties.length ? setItem(p.properties) : []
        }
      })
  }

  return setItem(properties)
}

export function prepareForFormulaInput(i18n: VueI18n, properties: Array<any>): Array<SelectOptionData> {
  const propertyOption = (properties: Array<any>): Array<SelectOptionData> => {
    return properties
      .map(p => {
        return {
          label: getPropertyTranslation(i18n, p.name ?? p.type),
          value: p.placeholderPath,
        }
      })
  }

  return propertyOption(properties)
}

export function extraView(type: PropertyFieldType | string) {
  return BotPropertiesExtraData?.[type]?.settingView
}

